import {
  duplicateRecipients,
  isBareEmailAddress,
  MAX_RECIPIENTS,
} from '~/cynqueModules/emailRecipientRules'
import { EmailStatusIds } from '~/services/selectLists/emailStatii'
import type { IActivityEmail } from '~/types/email/models'

/**
 * The mock's half of `POST` / `PATCH /api/emails` (cynque-api-server #1770,
 * resources/api/emails/request_handlers.py and services/email_composition.py).
 *
 * WHY THIS EXISTS: the handler was the generic add/update, which stores anything
 * and answers 201/200. The server refuses Received, a `sentAt` on a draft, a send
 * it cannot make (with every reason at once), a suppressed recipient, and any
 * change to a message's envelope. A mock that accepts all of that hides exactly
 * the failures the webapp has to present -- the lesson of versionConflict.ts.
 *
 * Wording is the server's, so the webapp is exercised against what it sends.
 */

/** Where the mock lets From be. The server's is EMAIL_SENDING_DOMAINS. */
export const MOCK_EMAIL_SENDING_DOMAINS = ['mail.mock.cynque.app']

// An email in one of these went some other way, so `sentAt` is the user's.
const RECORDED: number[] = [
  EmailStatusIds.SENT,
  EmailStatusIds.NEEDS_FOLLOW_UP,
  EmailStatusIds.CLOSED,
]
// Where a message may be moved by hand.
const FOR_A_MESSAGE: number[] = [
  EmailStatusIds.NEEDS_FOLLOW_UP,
  EmailStatusIds.CLOSED,
]
// What a message keeps (create_email_store's emails_keep_message_envelope).
const ENVELOPE_KEYS: (keyof IActivityEmail)[] = [
  'fromAddress',
  'fromName',
  'toAddresses',
  'ccAddresses',
  'bccAddresses',
  'replyTo',
  'subject',
  'body',
]

// A link the server counts as an email's SENDING RECORD, in its order.
const SENDING_ENTITY_ORDER = [
  'order',
  'opportunity',
  'company',
  'supplier',
  'tsd',
] as const

export interface IRefusal {
  status: 400 | 409
  body: Record<string, unknown>
}

function badRequest(
  title: string,
  detail: string,
  extra: Record<string, unknown> = {}
): IRefusal {
  // ProblemDetails, with `extra` merged into the top level as litestar does.
  return { status: 400, body: { status: 400, title, detail, ...extra } }
}

function conflict(title: string, detail: string): IRefusal {
  return { status: 409, body: { status: 409, title, detail } }
}

const statusIdOf = (email: Partial<IActivityEmail>) =>
  email.status?.id as number | undefined

export const isMockMessage = (email: Partial<IActivityEmail>) =>
  !!email.direction

/** The status rules that do not depend on what the email was before. */
export function statusRefusal({
  statusId,
  sentAt,
  isMessage,
}: {
  statusId: number | undefined
  sentAt: unknown
  isMessage: boolean
}): IRefusal | undefined {
  if (statusId === EmailStatusIds.RECEIVED) {
    return badRequest(
      'Status not allowed',
      'Received is the status of mail that arrived; nobody sets it.'
    )
  }
  if (sentAt != null && !isMessage && !RECORDED.includes(statusId as number)) {
    return badRequest(
      'sentAt not allowed',
      'sentAt records when an email went some other way, so it belongs only to ' +
        'one at Sent, Needs Follow Up or Closed. An email sent from here gets its ' +
        'time when Postmark accepts it.'
    )
  }
  return undefined
}

/** Everything stopping this email being sent, or nothing. */
export function compositionProblems(
  email: Partial<IActivityEmail>,
  sendingDomains: string[] = MOCK_EMAIL_SENDING_DOMAINS
): string[] {
  const found: string[] = []
  const to = email.toAddresses ?? []
  const cc = email.ccAddresses ?? []
  const bcc = email.bccAddresses ?? []
  const from = email.fromAddress
  if (!from) {
    found.push('From is required.')
  } else if (!isBareEmailAddress(from)) {
    found.push('From must be a bare email address (local@domain).')
  } else if (
    !sendingDomains.includes(from.split('@').pop()?.toLowerCase() ?? '')
  ) {
    found.push(`From must be at ${sendingDomains.join(', ')}.`)
  }
  if (!to.length) {
    found.push('At least one To recipient is required.')
  }
  if (![...to, ...cc, ...bcc].every((a) => isBareEmailAddress(a))) {
    found.push('Every recipient must be a bare email address (local@domain).')
  }
  if (duplicateRecipients(to, cc, bcc).length) {
    found.push('Each recipient may appear once across To, Cc and Bcc.')
  }
  if (to.length + cc.length + bcc.length > MAX_RECIPIENTS) {
    found.push(`At most ${MAX_RECIPIENTS} recipients across To, Cc and Bcc.`)
  }
  if (email.replyTo && !isBareEmailAddress(email.replyTo)) {
    found.push('Reply-To must be a bare email address (local@domain).')
  }
  const subject = email.subject ?? ''
  if (!subject.trim()) {
    found.push('A subject is required.')
    // eslint-disable-next-line no-control-regex
  } else if (/[\x00-\x1f\x7f]/.test(subject)) {
    found.push('The subject must be a single line with no control characters.')
  }
  if (!(email.body ?? '').trim()) {
    found.push('A body is required.')
  }
  return found
}

/** Why a SEND of this (stored) email is refused, or undefined. */
export function sendRefusal(
  email: Partial<IActivityEmail>
): IRefusal | undefined {
  if (email.case) {
    return badRequest(
      'Email is on a case',
      `An email on a case is sent as a case reply: POST /api/cases/${email.case.id}/replies.`
    )
  }
  const problems = compositionProblems(email)
  if (problems.length) {
    return badRequest('Email cannot be sent', problems.join(' '), { problems })
  }
  return undefined
}

/** A refused change to a MESSAGE (sent or received), or undefined. */
export function messageChangeRefusal({
  id,
  stored,
  changes,
}: {
  id: string
  stored: Partial<IActivityEmail>
  changes: Partial<IActivityEmail>
}): IRefusal | undefined {
  if (!isMockMessage(stored)) {
    return undefined
  }
  const changed = (key: keyof IActivityEmail) =>
    key in changes &&
    JSON.stringify(changes[key] ?? null) !== JSON.stringify(stored[key] ?? null)
  if (ENVELOPE_KEYS.some(changed)) {
    return conflict(
      'Already sent or received',
      `EMail=${id} was already sent or received; what it says cannot change.`
    )
  }
  const statusId = statusIdOf(changes)
  if (
    statusId !== undefined &&
    statusId !== statusIdOf(stored) &&
    !FOR_A_MESSAGE.includes(statusId)
  ) {
    return conflict(
      'Already sent or received',
      `EMail=${id} was already sent or received; it can be moved only to Needs` +
        ' Follow Up or Closed.' +
        (statusId === EmailStatusIds.READY_TO_SEND
          ? ' Sending it again is a resend.'
          : '')
    )
  }
  if (changed('sentAt')) {
    return conflict(
      'Already sent or received',
      `EMail=${id}'s sentAt is when Postmark accepted it.`
    )
  }
  return undefined
}

/** The record a new file is filed with, or undefined when none is linked. */
export function sendingRecord(
  email: Partial<IActivityEmail>
): { kind: string; id: string } | undefined {
  for (const kind of SENDING_ENTITY_ORDER) {
    const link = email[kind] as { id?: string } | undefined
    if (link?.id) {
      return { kind, id: link.id }
    }
  }
  return undefined
}

/**
 * The server's refusal of a documentId that is not a document of the email's
 * sending record. It does not say which: "not this record's" and "does not
 * exist" would differ only in whether the id exists elsewhere.
 */
export function unknownDocument(
  kind: string,
  matched: number,
  requested: number
): IRefusal {
  return badRequest(
    'Unknown document',
    `Every documentId must be a document of this email's ${kind}. ` +
      `${matched} of ${requested} matched.`
  )
}

export function nothingToAttachFrom(): IRefusal {
  return badRequest(
    'Nothing to attach from',
    'An email attaches documents of the record it is sent from: link an ' +
      'order, opportunity, company, supplier or TSD.'
  )
}

/**
 * The fields a send writes: an outbound message, queued. It STAYS queued and
 * at Ready to Send, as the server's response has it -- nothing in the mock
 * sends (see emailForPost.ts queuedEmailMessage).
 */
export function queuedMessageFields(id: string): Partial<IActivityEmail> {
  return {
    direction: 'outbound',
    deliveryState: 'queued',
    messageId: `<${id}@${MOCK_EMAIL_SENDING_DOMAINS[0]}>`,
    providerMessageId: null,
    sentAt: null,
    // The server's delete guard: a message is the record of what was said.
    canBeDeleted: false,
  }
}
